import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { AgeStatusFieldComponent } from '@shared/components/age-status-field/age-status-field.component';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { MemberLinkSourceComponent } from '@shared/components/member-link-source/member-link-source.component';
import { NotificationService } from '@shared/services/notification.service';
import { DateFormatterService } from '@shared/services/date-formatter.service';
import { normalizePhoneNumberToE164, phoneNumberValidator } from '@shared/validators/phone-number.validator';
import { formatPhoneNumberForDisplay, formatPhoneNumberWhileTyping } from '@shared/utils/phone-number-format.util';
import { PersonService } from '../services/person.service';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { Person } from '../models/person.model';
import { CatalogApiService, CountryDto } from '@core/api/services/catalog-api.service';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { getApiErrorMessage, getApiValidationMessage } from '@core/http/api-error-message.util';
import { Observable, Subject, BehaviorSubject, EMPTY, combineLatest, firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap, finalize, startWith } from 'rxjs/operators';
import { documentTypeOptions, genderOptions, personAgeStatusOptions, relationshipSituationOptions } from '@core/api/enums';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { DocumentType } from '@core/api/generated/api-client';
import { UserService } from '@features/user/services/user.service';
import { PersonLookupOption } from '../models/person-event.model';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { PersonStatusDialogComponent, PersonStatusDialogResult } from '../components/person-status-dialog.component';
import { RelationshipSyncDialogComponent, RelationshipSyncDialogResult } from '../components/relationship-sync-dialog.component';

interface PersonEditViewModel {
  person: Person | null;
  personName: string;
  countries: CountryDto[];
  isNewMode: boolean;
  authorizedUnits: OrganizationUnitItem[];
  membershipUnitReadonly: boolean;
}

@Component({
  selector: 'app-person-edit-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    MatExpansionModule,
    MatCardModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    AgeStatusFieldComponent,
    DatePickerComponent,
    UnitSelectorComponent,
    MemberLinkSourceComponent
  ],
  templateUrl: './person-edit-page.component.html',
  styleUrl: './person-edit-page.component.scss'
})
export class PersonEditPageComponent implements OnDestroy {
  private static readonly DEFAULT_DOCUMENT_TYPE: DocumentType = 'Dni';
  readonly vm$: Observable<PersonEditViewModel | null>;
  readonly isSaving$ = new BehaviorSubject<boolean>(false);
  readonly error$ = new BehaviorSubject<string | null>(null);

  readonly personForm: FormGroup;
  readonly countryOfBirthSearchControl = new FormControl<CountryDto | string | null>(null);
  readonly nationalitySearchControl = new FormControl<CountryDto | string | null>(null);
  readonly relationshipPersonSearchControl = new FormControl<PersonLookupOption | string | null>(null);
  readonly filteredRelationshipPeople$: Observable<PersonLookupOption[]>;
  readonly maxBirthDate: Date;

  readonly documentTypes = documentTypeOptions;
  readonly genders = genderOptions;
  readonly relationshipSituations = relationshipSituationOptions;
  readonly ageStatusOptions = personAgeStatusOptions;

  private static readonly COMMUNITY_LEVEL_ID = 5;

  get birthDateControl(): FormControl {
    return this.personForm.get('birthDate') as FormControl;
  }

  get ageStatusOverrideControl(): FormControl {
    return this.personForm.get('ageStatusOverride') as FormControl;
  }

  private personId = '';
  private identityUserId: string | null = null;
  private isNewMode = false;
  canManageMemberData = false;
  private relationshipPersonWasSelected = false;
  private originalRelationshipSituation: string | undefined;
  private originalRelationshipPersonId: string | undefined;
  private relationshipPersonToUpdateId: string | undefined;
  private newModeReturnUrl: string[] = ['/'];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly personService: PersonService,
    private readonly userService: UserService,
    private readonly catalogApi: CatalogApiService,
    private readonly notifications: NotificationService,
    private readonly dateFormatter: DateFormatterService,
    private readonly dialog: MatDialog,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly userFullInfoService: UserFullInfoService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.maxBirthDate = this.dateFormatter.getTodayAsDate();
    this.personForm = this.buildForm();
    this.filteredRelationshipPeople$ = this.relationshipPersonSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value !== 'string' || value.trim().length < 2) {
          return of([]);
        }

        return this.personService.searchPeople(value.trim());
      }),
    );
    this.relationshipPersonSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (typeof value === 'string') {
          this.personForm.get('relationshipPersonId')?.setValue(null, { emitEvent: false });
          this.personForm.get('relationshipPersonName')?.setValue(value, { emitEvent: false });
          this.relationshipPersonWasSelected = false;
        }
      });
    this.personForm.get('relationshipSituation')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(situation => {
        if (this.isRelationshipWithoutLinkedPerson(situation)) {
          this.personForm.patchValue({ relationshipPersonId: null, relationshipPersonName: '' }, { emitEvent: false });
          this.relationshipPersonSearchControl.setValue('', { emitEvent: false });
          this.relationshipPersonWasSelected = false;
        }
      });
    this.setupCountryAutocompleteControls();
    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.canManageMemberData = this.userFullInfoService.canManageMemberDataSnapshot(user);
        this.cdr.markForCheck();
      });
    this.vm$ = this.initViewModel();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async save(): Promise<void> {
    if (!this.canManageMemberData) {
      this.notifications.error('No tenes autorizacion para modificar datos de miembros.');
      return;
    }

    await this.normalizePhoneControlForSave();

    if (!this.personForm.valid) {
      this.personForm.markAllAsTouched();
      this.notifications.warning('Por favor, completá todos los campos requeridos correctamente');
      this.focusFirstInvalidControl();
      return;
    }

    this.isSaving$.next(true);
    const formValue = this.personForm.getRawValue();
    const birthDate = formValue.birthDate
      ? this.dateFormatter.toISODateString(formValue.birthDate)
      : null;

    if (formValue.birthDate && !birthDate) {
      this.birthDateControl.setErrors({ matDatepickerParse: true });
      this.birthDateControl.markAsTouched();
      this.notifications.error('La fecha de nacimiento no es valida');
      this.isSaving$.next(false);
      return;
    }

    if (!await this.ensureWidowedRelationshipIsConsistent(formValue)) {
      this.isSaving$.next(false);
      return;
    }

    if (this.isNewMode) {
      const createData = this.buildCleanPayload({ ...formValue, birthDate });

      const createRequest$ = this.identityUserId
        ? this.userService.createMember(this.identityUserId, createData as any).pipe(
            switchMap(personId => this.personService.getPerson(personId)))
        : this.personService.createPerson(createData as any);

      createRequest$.pipe(
        tap(person => {
          this.notifications.success('Persona creada correctamente');
          const membershipUnitId = person.communityId || createData['membershipUnitId'];
          if (membershipUnitId) {
            this.router.navigate(['/units', membershipUnitId, 'members']);
          } else {
            this.router.navigate(['/people', person.id]);
          }
        }),
        catchError(error => {
          console.error('Error creating person:', error);
          this.applyBackendBirthDateError(error);
          this.notifications.error(this.getSaveErrorMessage(
            error,
            'No se pudo crear la persona. Revisá si el documento ya existe o si los datos cargados son correctos'
          ));
          return EMPTY;
        }),
        finalize(() => this.isSaving$.next(false)),
        takeUntil(this.destroy$)
      ).subscribe();
    } else {
      const updateRelationshipPerson = await this.confirmRelationshipSync(formValue);
      if (updateRelationshipPerson === null) {
        this.isSaving$.next(false);
        return;
      }

      const updateData = this.buildCleanPayload({
        ...formValue,
        birthDate,
        updateRelationshipPerson,
        relationshipPersonToUpdateId: this.relationshipPersonToUpdateId,
      });

      this.personService.updatePerson(this.personId, updateData as any).pipe(
        tap(() => {
          this.notifications.success('Persona actualizada correctamente');
          const allParams = this.route.snapshot.pathFromRoot.reduce(
            (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
          );
          const unitId = allParams['unit-id'];
          this.router.navigate(
            unitId
              ? ['/units', unitId, 'members', this.personId]
              : ['/people', this.personId]
          );
        }),
        catchError(error => {
          console.error('Error updating person:', error);
          this.applyBackendBirthDateError(error);
          this.notifications.error(this.getSaveErrorMessage(
            error,
            'No se pudo actualizar la persona. Revisá si el documento ya existe o si los datos cargados son correctos'
          ));
          return EMPTY;
        }),
        finalize(() => this.isSaving$.next(false)),
        takeUntil(this.destroy$)
      ).subscribe();
    }
  }

  /**
   * Construye un payload limpio omitiendo propiedades null, undefined y strings vacíos.
   * El backend recibe únicamente los campos que el usuario completó.
   */
  private buildCleanPayload(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== null && v !== undefined && v !== '')
    );
  }

  cancel(): void {
    if (this.isNewMode || !this.personId) {
      this.router.navigate(this.newModeReturnUrl);
      return;
    }

    this.router.navigate(
      ['/people', this.personId]
    );
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      // Unidad (solo requerida en modo creación, se habilita/deshabilita en initViewModel)
      membershipUnitId: [null],
      // Identificación
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      documentType: [PersonEditPageComponent.DEFAULT_DOCUMENT_TYPE],
      documentNumber: ['', [Validators.maxLength(50)]],
      // Datos personales
      birthDate: [null, [this.notFutureBirthDateValidator()]],
      gender: [null],
      relationshipSituation: ['Unspecified', [Validators.required]],
      relationshipPersonId: [null],
      relationshipPersonName: ['', [Validators.maxLength(200)]],
      placeOfBirth: ['', [Validators.maxLength(200)]],
      countryOfBirthId: [null],
      nationalityId: [null],
      languages: ['', [Validators.maxLength(500)]],
      ageStatusOverride: [null],
      // Contacto
      email: ['', [Validators.email]],
      phone: ['', [Validators.maxLength(20)], [phoneNumberValidator()]],
      address: ['', [Validators.maxLength(250)]],
      // Otros
      occupation: ['', [Validators.maxLength(150)]],
      previousReligion: ['', [Validators.maxLength(150)]],
    });
  }

  private initViewModel(): Observable<PersonEditViewModel | null> {
    const countries$ = this.catalogApi.getCountries().pipe(catchError(() => EMPTY));

    return this.route.paramMap.pipe(
      switchMap(paramMap => {
        const id = paramMap.get('person-id');

        if (!id) {
          // Modo creación: membershipUnitId es requerido
          this.isNewMode = true;
          this.personId = '';
          this.error$.next(null);
          this.personForm.get('membershipUnitId')!.setValidators([Validators.required]);
          this.personForm.get('membershipUnitId')!.updateValueAndValidity();

          const authorizedUnits$ = this.personService.getAuthorizedUnits().pipe(catchError(() => EMPTY));
          const queryParams$ = this.route.queryParamMap;

          return combineLatest([countries$, authorizedUnits$, queryParams$]).pipe(
            tap(([, authorizedUnits, queryParams]) => {
              const communities = authorizedUnits.filter(
                u => u.levelId === PersonEditPageComponent.COMMUNITY_LEVEL_ID
              );
              const unitControl = this.personForm.get('membershipUnitId')!;
              this.identityUserId = queryParams.get('identityUserId');
              const identityEmail = queryParams.get('identityEmail');
              if (this.identityUserId && identityEmail) {
                this.personForm.get('email')!.setValue(identityEmail);
                this.personForm.get('email')!.disable();
              }
              const requestedUnitId = queryParams.get('unitId');
              const requestedAuthorizedUnit = requestedUnitId
                ? authorizedUnits.find(u => u.id === requestedUnitId) ?? null
                : null;
              const availableCommunities = this.filterCommunitiesForSource(
                communities,
                requestedAuthorizedUnit,
                authorizedUnits
              );

              this.configureNewMemberNavigation(requestedAuthorizedUnit);

              if (availableCommunities.length === 1) {
                unitControl.setValue(availableCommunities[0].id);
                unitControl.disable();
                return;
              }

              unitControl.enable();
              unitControl.reset();

              if (availableCommunities.length === 0) {
                this.notifications.error('No tenés comunidades autorizadas para crear miembros');
                return;
              }

              const requestedCommunity = requestedUnitId
                ? availableCommunities.find(u => u.id === requestedUnitId)
                : null;

              if (requestedCommunity) {
                unitControl.setValue(requestedCommunity.id);
                return;
              }

              const requestedUnitIsAuthorized = requestedUnitId
                ? !!requestedAuthorizedUnit
                : true;

              if (requestedUnitId && !requestedUnitIsAuthorized) {
                this.notifications.error(
                  'La unidad especificada no existe o no tenés permisos sobre ella'
                );
              }
            }),
            map(([countries, authorizedUnits, queryParams]) => {
              this.syncCountryAutocompleteDisplays(countries);
              const requestedUnitId = queryParams.get('unitId');
              const requestedAuthorizedUnit = requestedUnitId
                ? authorizedUnits.find(u => u.id === requestedUnitId) ?? null
                : null;
              const communities = authorizedUnits.filter(
                u => u.levelId === PersonEditPageComponent.COMMUNITY_LEVEL_ID
              );
              const availableCommunities = this.filterCommunitiesForSource(
                communities,
                requestedAuthorizedUnit,
                authorizedUnits
              );

              return {
                person: null,
                personName: 'Nuevo Miembro',
                countries,
                isNewMode: true,
                authorizedUnits: availableCommunities,
                membershipUnitReadonly: availableCommunities.length === 1,
              };
            }),
          );
        }

        // Modo edición
        this.isNewMode = false;
        this.personId = id;
        this.error$.next(null);

        const person$ = this.personService.getPerson(id).pipe(
          tap(person => {
            this.populateForm(person);
            this.breadcrumbService.setDynamicCrumb('unit-id', person.communityName);
            this.breadcrumbService.setDynamicCrumb('person-id', `${person.firstName} ${person.lastName}`);
          }),
          catchError(error => {
            console.error('Error loading person:', error);
            this.error$.next('Error al cargar la persona');
            return EMPTY;
          }),
        );

        return combineLatest([person$, countries$]).pipe(
          map(([person, countries]) => {
            this.syncCountryAutocompleteDisplays(countries);

            return {
              person,
              personName: `${person.firstName} ${person.lastName}`,
              countries,
              isNewMode: false,
              authorizedUnits: [],
              membershipUnitReadonly: false,
            };
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private populateForm(person: Person): void {
    this.personForm.patchValue({
      firstName:        person.firstName      ?? '',
      lastName:         person.lastName       ?? '',
      documentType:     person.documentType   ?? PersonEditPageComponent.DEFAULT_DOCUMENT_TYPE,
      documentNumber:   person.documentNumber ?? '',
      birthDate:        person.dateOfBirth ?? null,
      gender:           person.gender          ?? null,
      relationshipSituation: person.relationshipSituation ?? 'Unspecified',
      relationshipPersonId: person.relationshipPersonId ?? null,
      relationshipPersonName: person.relationshipPersonId ? '' : person.relationshipPersonName ?? '',
      occupation:       person.occupation      ?? '',
      previousReligion: person.previousReligion ?? '',
      placeOfBirth:     person.placeOfBirth    ?? '',
      countryOfBirthId: person.countryOfBirthId ?? null,
      nationalityId:    person.nationalityId   ?? null,
      languages:        person.languages       ?? '',
      ageStatusOverride: person.ageStatusOverride ?? null,
      email:            person.email    ?? '',
      phone:            formatPhoneNumberForDisplay(person.phone) ?? person.phone ?? '',
      address:          person.address  ?? '',
    });
    this.relationshipPersonSearchControl.setValue(person.relationshipPersonName ?? '', { emitEvent: false });
    this.relationshipPersonWasSelected = false;
    this.originalRelationshipSituation = person.relationshipSituation;
    this.originalRelationshipPersonId = person.relationshipPersonId;
  }

  private async confirmRelationshipSync(formValue: Record<string, unknown>): Promise<boolean | null> {
    const relationshipSituation = formValue['relationshipSituation'];
    const relationshipPersonId = typeof formValue['relationshipPersonId'] === 'string'
      ? formValue['relationshipPersonId']
      : this.isRelationshipWithoutLinkedPerson(relationshipSituation)
        ? this.originalRelationshipPersonId
        : undefined;
    const relationshipSituationChanged = relationshipSituation !== this.originalRelationshipSituation;
    this.relationshipPersonToUpdateId = undefined;
    if (typeof relationshipPersonId !== 'string' ||
      !this.isRelationshipSituationConfirmable(relationshipSituation) ||
      (!this.relationshipPersonWasSelected && !relationshipSituationChanged)) {
      return false;
    }

    try {
      const relationshipPerson = await firstValueFrom(this.personService.getPerson(relationshipPersonId));
      const user = this.userFullInfoService.getUserInfoSnapshot();
      const canUpdateRelatedPerson = !!(
        relationshipSituation !== 'Widowed' &&
        (user?.isAdmin ||
          (relationshipPerson.communityId && user?.manageableUnitIds.some(unitId =>
            unitId.toLowerCase() === relationshipPerson.communityId.toLowerCase())))
      );
      const result = await firstValueFrom(this.dialog.open(RelationshipSyncDialogComponent, modalDialogConfig({
        size: 'sm',
        layout: 'confirm',
        data: {
          relatedPersonName: `${relationshipPerson.firstName} ${relationshipPerson.lastName}`,
          canUpdateRelatedPerson,
        },
      })).afterClosed()) as RelationshipSyncDialogResult | undefined;

      if (result === 'cancel' || !result) {
        return null;
      }

      if (result === 'both' && this.isRelationshipWithoutLinkedPerson(relationshipSituation)) {
        this.relationshipPersonToUpdateId = relationshipPersonId;
      }

      return result === 'both';
    } catch (error) {
      console.error('Error checking related person access:', error);
      this.notifications.error('No se pudo comprobar el permiso para actualizar a la persona vinculada.');
      return null;
    }
  }

  private isReciprocalRelationshipSituation(situation: unknown): boolean {
    return situation === 'Married' || situation === 'CivilUnion' || situation === 'Concubinage';
  }

  private isRelationshipEnding(situation: unknown): boolean {
    return situation === 'Separated' || situation === 'Divorced';
  }

  private isRelationshipWithoutLinkedPerson(situation: unknown): boolean {
    return situation === 'Unspecified' || situation === 'Single';
  }

  private isRelationshipSituationConfirmable(situation: unknown): boolean {
    return this.isRelationshipWithoutLinkedPerson(situation) || situation === 'Widowed' ||
      this.isReciprocalRelationshipSituation(situation) || this.isRelationshipEnding(situation);
  }

  private async ensureWidowedRelationshipIsConsistent(formValue: Record<string, unknown>): Promise<boolean> {
    if (formValue['relationshipSituation'] !== 'Widowed' || typeof formValue['relationshipPersonId'] !== 'string') {
      return true;
    }

    try {
      const relationshipPerson = await firstValueFrom(this.personService.getPerson(formValue['relationshipPersonId']));
      if (relationshipPerson.status === 'Departed') {
        return true;
      }

      const confirm = await firstValueFrom(this.dialog.open(ConfirmDialogComponent, modalDialogConfig({
        size: 'sm',
        layout: 'confirm',
        data: {
          title: 'Situación de pareja inconsistente',
          message: `${relationshipPerson.firstName} ${relationshipPerson.lastName} no figura como fallecido/a. ¿Deseás registrar su fallecimiento para continuar?`,
          confirmText: 'Registrar fallecimiento',
          cancelText: 'Cancelar',
        },
      })).afterClosed());

      if (!confirm) {
        return false;
      }

      const statusChange = await firstValueFrom(this.dialog.open(PersonStatusDialogComponent, modalDialogConfig({
        size: 'sm',
        layout: 'form',
        data: { status: relationshipPerson.status, targetStatus: 'Departed' },
      })).afterClosed()) as PersonStatusDialogResult | null | undefined;

      if (!statusChange) {
        return false;
      }

      await firstValueFrom(this.personService.updateStatus(relationshipPerson.id, statusChange));
      return true;
    } catch (error) {
      console.error('Error validating widowed relationship:', error);
      this.notifications.error('No se pudo registrar el fallecimiento de la persona vinculada.');
      return false;
    }
  }

  displayRelationshipPerson(option: PersonLookupOption | string | null): string {
    return typeof option === 'string' ? option : option?.name ?? '';
  }

  onRelationshipPersonSelected(option: PersonLookupOption): void {
    this.personForm.patchValue({ relationshipPersonId: option.id, relationshipPersonName: '' }, { emitEvent: false });
    this.relationshipPersonSearchControl.setValue(option, { emitEvent: false });
    this.relationshipPersonWasSelected = true;
  }

  clearRelationshipPerson(): void {
    this.personForm.patchValue({ relationshipPersonId: null, relationshipPersonName: '' }, { emitEvent: false });
    this.relationshipPersonSearchControl.setValue('', { emitEvent: false });
    this.relationshipPersonWasSelected = false;
  }

  private focusFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalid = document.querySelector(
        'input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid, .mat-mdc-select.ng-invalid, [formcontrolname].ng-invalid'
      ) as HTMLElement | null;

      if (!firstInvalid) {
        return;
      }

      const focusTarget = (firstInvalid.matches('input, textarea, select')
        ? firstInvalid
        : firstInvalid.querySelector('input, textarea, button, [tabindex]')) as HTMLElement | null;

      (focusTarget ?? firstInvalid).focus();
      (focusTarget ?? firstInvalid).scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private configureNewMemberNavigation(sourceUnit: OrganizationUnitItem | null): void {
    if (!sourceUnit) {
      this.newModeReturnUrl = ['/'];
      this.breadcrumbService.setCustomBreadcrumbs([
        { label: 'Nuevo miembro', url: '/people/new' },
      ]);
      return;
    }

    this.newModeReturnUrl = ['/units', sourceUnit.id];
    this.breadcrumbService.setCustomBreadcrumbs([
      { label: 'Comunidades', url: '/units' },
      { label: sourceUnit.name, url: `/units/${sourceUnit.id}` },
      { label: 'Nuevo miembro', url: '/people/new' },
    ]);
  }

  formatPhoneControlForDisplay(): void {
    const phoneControl = this.personForm.get('phone');
    const formattedPhone = formatPhoneNumberForDisplay(phoneControl?.value);

    if (!phoneControl || !formattedPhone) {
      return;
    }

    phoneControl.setValue(formattedPhone);
  }

  formatPhoneControlWhileTyping(event: Event): void {
    const phoneControl = this.personForm.get('phone');
    const input = event.target as HTMLInputElement | null;

    if (!phoneControl || !input) {
      return;
    }

    const formattedPhone = formatPhoneNumberWhileTyping(input.value);

    if (formattedPhone === input.value) {
      return;
    }

    phoneControl.setValue(formattedPhone, { emitEvent: false });
    input.value = formattedPhone;
    input.setSelectionRange(formattedPhone.length, formattedPhone.length);
  }

  filterCountries(countries: CountryDto[], value: CountryDto | string | null): CountryDto[] {
    const search = typeof value === 'string'
      ? value.trim().toLowerCase()
      : '';

    if (!search) {
      return countries;
    }

    return countries.filter(country =>
      country.name?.toLowerCase().includes(search) ||
      country.iso2Code?.toLowerCase().includes(search) ||
      country.iso3Code?.toLowerCase().includes(search)
    );
  }

  displayCountry(country: CountryDto | string | null): string {
    if (!country) {
      return '';
    }

    return typeof country === 'string' ? country : country.name ?? '';
  }

  onCountryOfBirthSelected(event: MatAutocompleteSelectedEvent): void {
    this.setCountrySelection('countryOfBirthId', this.countryOfBirthSearchControl, event.option.value);
  }

  onNationalitySelected(event: MatAutocompleteSelectedEvent): void {
    this.setCountrySelection('nationalityId', this.nationalitySearchControl, event.option.value);
  }

  private async normalizePhoneControlForSave(): Promise<void> {
    const phoneControl = this.personForm.get('phone');

    if (!phoneControl) {
      return;
    }

    const normalizedPhone = await normalizePhoneNumberToE164(phoneControl.value);

    if (!normalizedPhone && phoneControl.value?.trim()) {
      phoneControl.setErrors({
        ...(phoneControl.errors ?? {}),
        invalidPhone: true,
      });
      return;
    }

    if (normalizedPhone) {
      phoneControl.setValue(normalizedPhone);

      if (phoneControl.pending) {
        await firstValueFrom(phoneControl.statusChanges.pipe(
          filter(status => status !== 'PENDING'),
          take(1)
        ));
      }
    }
  }

  private setupCountryAutocompleteControls(): void {
    this.countryOfBirthSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (typeof value === 'string') {
          this.personForm.get('countryOfBirthId')?.setValue(null);
        }
      });

    this.nationalitySearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (typeof value === 'string') {
          this.personForm.get('nationalityId')?.setValue(null);
        }
      });
  }

  private setCountrySelection(
    controlName: 'countryOfBirthId' | 'nationalityId',
    searchControl: FormControl<CountryDto | string | null>,
    country: CountryDto | null
  ): void {
    this.personForm.get(controlName)?.setValue(country?.id ?? null);
    searchControl.setValue(country, { emitEvent: false });
  }

  private syncCountryAutocompleteDisplays(countries: CountryDto[]): void {
    this.countryOfBirthSearchControl.setValue(
      this.findCountryById(countries, this.personForm.get('countryOfBirthId')?.value),
      { emitEvent: false }
    );
    this.nationalitySearchControl.setValue(
      this.findCountryById(countries, this.personForm.get('nationalityId')?.value),
      { emitEvent: false }
    );
  }

  private findCountryById(countries: CountryDto[], countryId: number | null | undefined): CountryDto | null {
    if (!countryId) {
      return null;
    }

    return countries.find(country => country.id === countryId) ?? null;
  }

  private notFutureBirthDateValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const date = this.dateFormatter.toLocalDate(value);

      if (!date || Number.isNaN(date.getTime())) {
        return null;
      }

      return date > this.maxBirthDate ? { futureBirthDate: true } : null;
    };
  }

  private applyBackendBirthDateError(error: any): void {
    const birthDateError = getApiValidationMessage(error, 'BirthDate');

    if (!birthDateError) {
      return;
    }

    this.birthDateControl.setErrors({
      ...(this.birthDateControl.errors ?? {}),
      futureBirthDate: true,
    });
    this.birthDateControl.markAsTouched();
    this.focusFirstInvalidControl();
  }

  private getSaveErrorMessage(error: any, fallback: string): string {
    return getApiErrorMessage(error, fallback);
  }

  private filterCommunitiesForSource(
    communities: OrganizationUnitItem[],
    sourceUnit: OrganizationUnitItem | null,
    allAuthorizedUnits: OrganizationUnitItem[]
  ): OrganizationUnitItem[] {
    if (!sourceUnit) {
      return communities;
    }

    if (sourceUnit.levelId === PersonEditPageComponent.COMMUNITY_LEVEL_ID) {
      return communities.filter(community => community.id === sourceUnit.id);
    }

    return communities.filter(community =>
      this.isDescendantOf(community, sourceUnit.id, allAuthorizedUnits)
    );
  }

  private isDescendantOf(
    unit: OrganizationUnitItem,
    ancestorId: string,
    allUnits: OrganizationUnitItem[]
  ): boolean {
    const unitsById = new Map(allUnits.map(item => [item.id, item]));
    let parentId = unit.parentId;

    while (parentId) {
      if (parentId === ancestorId) {
        return true;
      }

      parentId = unitsById.get(parentId)?.parentId;
    }

    return false;
  }
}
